/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { SSRDataJson } from '../types';
import { pd } from '@airasia-phoenix/react-native/tokens';

// Room variant constants
export const ROOM_VARIANTS = {
  BOTTOM_THUMBNAILS: 'bottom-thumbnails' as const,
  RIGHT_THUMBNAILS: 'right-thumbnails' as const,
} as const;

export const PREMIUM_ROOM_KEYWORDS = ['suite', 'premium', 'deluxe'] as const;

export type RoomVariantType =
  (typeof ROOM_VARIANTS)[keyof typeof ROOM_VARIANTS];

export const HOTEL_CHECKOUT_TOKEN_ENDPOINT = 'checkout';
export const HOTEL_BOOKING_TOKEN_ENDPOINT = 'booking';
export const HOTEL_REVIEW_TOKEN_ENDPOINT = 'review';

export const SSR_DATA: SSRDataJson = {
  synonyms: [
    {
      name: 'ALGO_ONE' as const,
      value: ['SSI5', 'SPAK', 'SMD7', 'SKLB', 'SXAI'],
    },
    {
      name: 'ALGO_TWO' as const,
      value: ['SRX5', 'SRDJ', 'SLAK', 'SKD7', 'SBZ2'],
    },
    {
      name: 'ALGO_THREE' as const,
      value: ['SPGR', 'SMDJ', 'SMXT', 'SZQZ', 'SLXT'],
    },
    {
      name: 'ALGO_FOUR' as const,
      value: ['SSXT', 'SLZ2', 'SRXT', 'SAI5', 'SBI5'],
    },
    {
      name: 'ALGO_FIVE' as const,
      value: ['SLD7', 'SQXJ', 'SOXT', 'SBFD', 'SYXT'],
    },
    {
      name: 'ALGO_SIX' as const,
      value: ['SLUM', 'SQAI', 'SORM', 'SBOH', 'SYAM'],
    },
  ],
  api_data: [
    {
      source: 'booking',
      id: 'sdlkd2l3',
    },
    {
      source: 'details',
      id: 'eue3kds',
    },
    {
      source: HOTEL_CHECKOUT_TOKEN_ENDPOINT,
      id: 'wxesdumi',
    },
    {
      source: 'search',
      id: 'xmncx278',
    },
    {
      source: 'smartfill',
      id: 'wiuwejk2',
    },
    {
      source: HOTEL_REVIEW_TOKEN_ENDPOINT,
      id: 'eue3kds',
    },
  ],
};

export const DEFAULT_ENDPOINT = 'smartfill';
export const DEFAULT_COUNTRY = 'MY';
export const DEFAULT_NEAREST_AIRPORT = 'KUL';
export const DEFAULT_STATIONTYPE = 'H';
export const TRAVEL_TYPE = 'airport';
export const HOTEL_TYPE = 'hotel';
export const ALLOWED_TYPES = [
  'airport',
  'hotel',
  'landmark',
  'neighborhood',
  'city',
];
export const RECENT_HS_LIST = 'recent.hs.list';
export const GENERAL = 'gnral';

export const TRACKING_CONSTANTS = {
  ACTIONS: {
    SELECT: 'aa_htl_recowidget_clicked',
    VIEW_ITEM_LIST: 'view_item_list',
  },

  // Common
  LINE_OF_BUSINESS: 'Hotels SA',
  SCREEN_NAME: 'Home',

  // Categories
  HOTEL_DEALS_CATEGORY: 'Hotels SA|Hotel Deals',
  RECENT_SEARCH_CATEGORY: 'Hotels SA|hotelsearchwiget',

  // Item list names
  HOTEL_LAST_SEARCHED_LIST: 'Hotel last searched',

  // Ecommerce keys (GA-style)
  CATEGORY: 'category',
  CURRENCY: 'currency',

  //SELECT specific keys
  RECOMMENDATION_TYPE: 'recommendationtype',
  HOTEL_ID: 'hotelid',
  HOTEL_NAME: 'hotelname',
  DESTINATION: 'destination',
  DESTINATION_CATEGORY: 'destinationcategory',
  DESTINATION_COUNTRY: 'destinationcountry',
  CHECK_IN_DATE: 'checkindate',
  CHECK_OUT_DATE: 'checkoutdate',
  LENGTH_OF_STAY: 'lengthofstay',
  ROOMS: 'rooms',
  TOTAL_PAX: 'totalpax',
  ADULT: 'adult',
  CHILD: 'child',
  INFANT: 'infant',
} as const;

export const RECOMMENDATIONS_MOCK = 'recommendationsMock';
export const FEATURE_DESTINATIONS = 'featureDestinations';
export const HOTELS_DOMAIN = 'hotels';
export const LOCALE = 'locale';
export const TARGETING_CODES = 'app-search-primary';
export const LOB = 'HOTEL';
export const MIN_VALUE = 0;
// MAX_VALUE is set to a high default to prevent hotels from being
// filtered out before actual price limits are calculated from API data.
export const MAX_VALUE = 1000000;
export const WEB_HEADER_HEIGHT = 90;
export const WEB_FOOTER_HEIGHT = 64;
export const MOBILE_VERTICAL_OFFSET = 96;
export const MIN_QUERY_LENGTH = 3;
export const BASE_PADDING_START = pd.ref.space.spacing40.px;
export const CHILD_PADDING_OFFSET = pd.ref.space.spacing80.px;
export const INITIAL_COUNT = 5;
export const SELECTION_MODE = {
  RADIO: 'radio',
};
export const FEATURE_DESTINATIONS_MOCK = 'featureDestinationsMock';
export const RECENT_SEARCHES_STORAGE_KEY = '@Hotel/recent_searches';
export const CONTINUE_SEARCHES_STORAGE_KEY = '@Hotel/continue_searches';
export const HOTEL_LAST_SEARCH_PARAMS_STORAGE_KEY = '@Hotel/last_search_params';
export const MAX_RECENT_SEARCHES = 3;
export const MAX_CONTINUE_SEARCHES = 3;
export const HOTEL = 'hotel';
export const CACHE_MAX_AGE = 2147483647; // Max 32-bit signed int (approx 24.8 days)

// Banner Constants
export const BANNER_HEIGHT = 148;
export const DEFAULT = 'default';
export const DROP_DOWN_HEIGHT = 392;
export const DROP_DOWN_WIDTH = 360;
export const DROP_DOWN_ITEM_HEIGHT = 64;
export const DROP_DOWN_HEADER_HEIGHT = 48;
export const DROP_DOWN_PORTAL_HOST_NAME = 'hotels-web-portal-host';
export const SWAP_BUTTON_SIZE = pd.ref.space.spacing60.px;

export const TRACK_WIDTH = 280;
export const CONTAINER_WIDTH = 32;
export const KNOB_VALUE = 10;
export const BANNER_DISPLAY_INDEX = 1;

/**
 * Guest picker and URL parsing share these caps.
 * A query value such as `rooms=991239577` must never become an array of that length.
 */
export const MAX_HOTEL_ROOMS = 12;
export const MAX_ADULTS_PER_ROOM = 12;
export const MAX_CHILDREN_PER_ROOM = 6;
export const MAX_CHILD_AGE = 17;

// Calendar Constants
export const MAX_LOS = 30;
export const MIN_DATE_OFFSET_DAYS = 0;
export const MIN_RETURN_DATE_OFFSET_DAYS = 1;
export const MAX_DATE_OFFSET_DAYS = 365;
export const DEFAULT_DEPARTURE_OFFSET_DAYS = 7;
export const DEFAULT_RETURN_OFFSET_DAYS = 9;
export const DEFAULT_STAY_DAYS = 2;
export const DEFAULT_ONE_WAY_STAY_DAYS = 1;
export const CALENDAR_BOTTOM_SHEET_HEIGHT = '80%';
export const MOBILE_WEB_HEADER_HEIGHT = 85;

// Trip Type Constants
export const TRIP_TYPES = {
  ONE_WAY: 'ONEWAY',
  ROUND_TRIP: 'ROUNDTRIP',
} as const;

export const HOTEL_SEARCH_DEFAULTS = {
  LOCATION_ID: '6351156',
  CHECKIN: '2026-03-16',
  CHECKOUT: '2026-03-18',
  CURRENCY: 'MYR',
  COUNTRY_CODE: 'MY',
  OCCUPANCY: '2',
  HOTELS_SIZE: 150,
  USER_SELECTED_HOTEL_ID: '',
  STAY_DAYS: 2,
} as const;

export const HOTEL_SEARCH_QUERY_PARAMS_TEMPLATE = {
  F_R: '',
  aaId: '',
  destination: '',
  rooms: '',
  adults: '',
  occupancy: '',
  applyBigpoints: 'false',
  fromDate: '',
  toDate: '',
  days: '',
  currency: '',
  countryCode: '',
  languageId: '',
} as const;

export const HOTEL_HEIGHT = 250;

export const CALENDAR_VIEW_CONFIG = {
  DROPDOWN_WIDTH: 752,
  EDGE_PADDING: 16,
  GAP: 4,
} as const;
export const FILTER_STATE_KEY = ['HOTEL_FILTERS_SELECTION'] as const;

export const SORT_BY = {
  DEFAULT: 'recommended',
  PRICE_LOW_TO_HIGH: 'lowToHigh',
  PRICE_HIGH_TO_LOW: 'highToLow',
  GUEST_RATING: 'topRating',
} as const;

export const PAYMENT_OPTIONS = [
  { label: 'Free cancellation', value: 'FREE_CANCELLATION' },
  { label: 'Pay at Hotel', value: 'PAY_AT_HOTEL' },
  { label: 'Pay Now', value: 'PAY_NOW' },
] as const;

export const FILTERS_REQUIRING_BACKEND_ID = ['amenities', 'category'] as const;

export const PROMOTIONS_FILTER_VALUE = 'Promotions & offers';

export const IMAGE_HEIGHT = 200;
export const IMAGE_WIDTH = 176;

export const CHINA_DISCLAIMER_IMAGE_URL =
  'https://static.airasia.com/flights/images/flightsNoResults.svg';

export const CHINA_DISCLAIMER_COUNTDOWN = 5;
export const CHINA_DOMAIN = 'airasia.cn';

// Old (legacy) hotel listing page base URLs
export const OLD_HOTEL_LIST_PAGE_BASE_URLS = {
  prod: 'https://www.airasia.com/hotel/search/index.html',
  stg: 'https://www.stgairasia.com/hotel/search/index.html',
} as const;

/**
 * Move hotels-web mount path on airasia.com / web.* hosts (EXPO_BASE_URL / reverse proxy).
 * Same `/hotel` path on all envs — only the domain changes.
 */
export const MOVE_HOTEL_WEB_BASE_PATH = '/hotel';

// New MOVE hotel listing page base URLs
export const MOVE_HOTEL_LIST_PAGE_BASE_URLS = {
  prod: `https://www.airasia.com${MOVE_HOTEL_WEB_BASE_PATH}/search`,
  stg: `https://web.stgairasia.com${MOVE_HOTEL_WEB_BASE_PATH}/search`,
} as const;

export const MOVE_HOTEL_WEB_HOSTS = {
  dev: `https://web.devairasia.com${MOVE_HOTEL_WEB_BASE_PATH}`,
  stg: `https://web.stgairasia.com${MOVE_HOTEL_WEB_BASE_PATH}`,
  prd: `https://www.airasia.com${MOVE_HOTEL_WEB_BASE_PATH}`,
} as const;

export const V2_ENABLED_COOKIE = 'v2_hotels_enabled';
export const HOTEL_SEARCH_URL_CHANGE_EVENT = 'hotel-search-url-change';
/** Set when mweb map edit opens search; consumed by search back to use history.back(). */
export const MAP_SEARCH_RETURN_AVAILABLE_KEY = 'map_search_return_available';
/** Full map URL to restore when the user backs out of map-edit search on mweb. */
export const MAP_EDIT_RETURN_URL_KEY = 'hotel_map_edit_return_url';

export const HOTEL_LOB = 'hotel' as const;
export const ADULTS_COUNT = 2;
export const CHILDREN = [];
export const INFANTS = 0;

// Room View Constants
export const AMENITY_ICON_TYPE = {
  HOTEL: 'hotel',
  ROOM: 'room',
  RATE: 'rate',
  OTHER: 'other',
} as const;

// Carousel Constants
export const MAX_CAROUSEL_INDICATORS = 10;
export const CAROUSEL_AUTO_SCROLL_INTERVAL = 2000;

// Image Resolution Keys
export const IMAGE_RESOLUTION_HIGH = '1000px';
export const IMAGE_RESOLUTION_LOW = '350px';

export const DEFAULT_MAP_OPTIONS = {
  initialLatLongs: {
    lat: 5.4219039133714,
    lon: 100.32541573048,
    latDelta: 0.0922,
    lonDelta: 0.0121,
  },
} as const;

export const RATING_SCALE = 5;

// Top Amenities Constants
export const AMENITIES_MOBILE_LIMIT = 5;
export const AMENITIES_DESKTOP_LIMIT = 9;
export const AMENITIES_MOBILE_MAX_CHARS = 18;
export const AMENITIES_DESKTOP_MAX_CHARS = 30;

// Sticky Bar Visibility Thresholds
export const VISIBILITY_THRESHOLD = {
  HERO: 0,
  FOOTER: 0.2,
  ROOMS: 0,
} as const;

// Web (desktop) — large buffers so the bar hides/shows ahead of the rooms section
export const ROOMS_STICKY_TOP_VISIBILITY_BUFFER_PX = 600;
export const ROOMS_STICKY_BOTTOM_VISIBILITY_BUFFER_PX = 750;

// Native (mobile) — no buffer needed; sentinel Y positions are exact pixel values
// in the scroll content coordinate space, so 0 gives the correct hide/show point.
export const ROOMS_STICKY_NATIVE_TOP_VISIBILITY_BUFFER_PX = 100;
export const ROOMS_STICKY_NATIVE_BOTTOM_VISIBILITY_BUFFER_PX = 200;

export const HOTEL_LIST_ROUTE = '/';

// Details View Constants
export const DETAILS_VIEW_MAX_WIDTH = {
  EXPLORE_AREA_MAP: 360,
  POI_CONTAINER: 380,
} as const;

export const DISCOUNT = 'DISCOUNT';
export const LOYALTY = 'LOYALTY';
